/**
 * MCP Host Service
 * Handles communication with MCP Host via AA Backend (Never direct!)
 * Flow: Frontend → AA Backend → MCP Host → External Servers
 */

import { apiClient } from './api';

export interface MCPHostStatus {
  status: 'connected' | 'connecting' | 'not_connected';
  host: string;
  port: number;
  version?: string;
  activeTools?: number;
  lastConnected?: Date | null;
  error?: string | null;
}

export interface MCPConnectionStatus {
  isRunning: boolean;
  connectedTools: MCPToolConnectionStatus[];
  overallStatus: 'connected' | 'disconnected' | 'partial' | 'error';
  lastUpdated: Date;
  error?: string | null;
}

export interface MCPToolConnectionStatus {
  name: string;
  enabled: boolean;
  status: 'connected' | 'disconnected' | 'error';
  lastConnected?: Date | null;
  error?: string | null;
  transport_type?: string;
}

export interface MCPHostTool {
  name: string;
  description: string;
  enabled: boolean;
  transport_type: string;
}

class MCPHostService {
  /**
   * Check MCP Host connection status via AA backend
   * NEVER makes direct requests to MCP Host
   * Uses dedicated status endpoint to check MCP Host connectivity
   */
  async checkConnection(): Promise<MCPHostStatus> {
    try {
      // Check MCP Host status through backend proxy
      const response = await apiClient.get('/api/external-tools/mcp-host/status');
      
      if (response.data?.success) {
        const data = response.data.data;
        if (data.status === 'connected') {
          return {
            status: 'connected',
            host: data.host || 'MCP Host',
            port: 8000,
            version: 'v1.0.0',
            activeTools: 0,
            lastConnected: new Date(),
          };
        } else if (data.status === 'error') {
          return {
            status: 'connecting',
            host: data.host || 'MCP Host',
            port: 8000,
            error: data.message || 'MCP Host returned error',
          };
        } else {
          return {
            status: 'not_connected',
            host: data.host || 'MCP Host',
            port: 8000,
            error: data.message || 'MCP Host not reachable',
          };
        }
      } else {
        return {
          status: 'not_connected',
          host: 'MCP Host',
          port: 8000,
          error: 'Backend service unavailable',
        };
      }
    } catch (error) {
      console.error('Failed to check MCP Host status:', error);
      return {
        status: 'not_connected',
        host: 'MCP Host',
        port: 8000,
        error: 'Cannot connect to backend',
      };
    }
  }

  /**
   * Get list of active tools from MCP Host via AA backend
   */
  async getActiveTools(): Promise<MCPHostTool[]> {
    try {
      const response = await apiClient.get('/mcp/tools');
      
      if (response.data?.success) {
        return response.data.data || [];
      }
      return [];
    } catch (error) {
      console.error('Failed to fetch MCP Host tools:', error);
      return [];
    }
  }

  /**
   * Sync user's selected tools with MCP Host via AA backend
   */
  async syncTools(tenantId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.post(`/api/external-tools/sync/${tenantId}`);
      
      if (response.data?.success) {
        return {
          success: true,
          message: response.data.message || 'Tools synchronized successfully',
        };
      } else {
        return {
          success: false,
          message: response.data?.message || 'Failed to sync tools',
        };
      }
    } catch (error) {
      console.error('Failed to sync tools with MCP Host:', error);
      return {
        success: false,
        message: 'Failed to sync tools with MCP Host',
      };
    }
  }

  /**
   * Test MCP Host connection with a specific tool via AA backend
   */
  async testToolConnection(toolName: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.post(`/mcp/tools/${toolName}/test`);
      
      if (response.data?.success) {
        return {
          success: true,
          message: response.data.message || 'Connection test successful',
        };
      } else {
        return {
          success: false,
          message: response.data?.message || 'Connection test failed',
        };
      }
    } catch (error) {
      console.error('Failed to test tool connection:', error);
      return {
        success: false,
        message: 'Failed to test tool connection',
      };
    }
  }

  /**
   * Start MCP Host via AA backend
   */
  async startMCPHost(): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.post('/mcp/start');
      
      if (response.data?.success) {
        return {
          success: true,
          message: response.data.message || 'MCP Host starting...',
        };
      } else {
        return {
          success: false,
          message: response.data?.message || 'Failed to start MCP Host',
        };
      }
    } catch (error) {
      console.error('Failed to start MCP Host:', error);
      return {
        success: false,
        message: 'Failed to start MCP Host',
      };
    }
  }

  /**
   * Get MCP connection status for all configured tools
   * This fetches the real MCP database status showing enabled vs actual connection status
   */
  async getMCPConnectionStatus(): Promise<MCPConnectionStatus> {
    try {
      // Get user ID from auth store
      const authStore = (await import('../stores/authStore')).useAuthStore.getState();
      const userId = authStore.user?.id;

      if (!userId) {
        throw new Error('User not authenticated');
      }

      // First, get user's configured tools from external-tools service
      const externalToolsService = (await import('./external-tools')).externalToolsService;
      const userTools = await externalToolsService.getUserTools(userId);

      // If user has no tools configured, return appropriate status
      if (!userTools || userTools.length === 0) {
        return {
          isRunning: false,
          connectedTools: [],
          overallStatus: 'disconnected',
          lastUpdated: new Date(),
          error: null
        };
      }

      // Check status for each configured tool
      const connectedTools: MCPToolConnectionStatus[] = [];

      for (const tool of userTools) {
        try {
          const response = await apiClient.get('/api/mcp/status', {
            params: {
              tenant_id: userId,
              mcp_server_id: tool.id
            }
          });

          if (response.data?.success && response.data?.data) {
            const status = response.data.data;
            connectedTools.push({
              name: tool.mcp_server_name,
              enabled: true,
              status: status.connected && status.initialized ? 'connected' :
                     status.error ? 'error' : 'disconnected',
              lastConnected: status.last_discovery ? new Date(status.last_discovery) : null,
              error: status.error_message || null,
              transport_type: tool.transport_type
            });
          } else {
            connectedTools.push({
              name: tool.mcp_server_name,
              enabled: true,
              status: 'disconnected',
              lastConnected: null,
              error: 'Failed to check status',
              transport_type: tool.transport_type
            });
          }
        } catch (error) {
          console.error(`Failed to check status for ${tool.mcp_server_name}:`, error);
          connectedTools.push({
            name: tool.mcp_server_name,
            enabled: true,
            status: 'error',
            lastConnected: null,
            error: 'Connection check failed',
            transport_type: tool.transport_type
          });
        }
      }

      // Determine overall status
      let overallStatus: 'connected' | 'disconnected' | 'partial' | 'error' = 'disconnected';

      if (connectedTools.length === 0) {
        overallStatus = 'disconnected';
      } else {
        const connectedCount = connectedTools.filter(tool => tool.status === 'connected').length;
        const errorCount = connectedTools.filter(tool => tool.status === 'error').length;

        if (errorCount === connectedTools.length) {
          overallStatus = 'error';
        } else if (connectedCount === 0) {
          overallStatus = 'disconnected';
        } else if (connectedCount === connectedTools.length) {
          overallStatus = 'connected';
        } else {
          overallStatus = 'partial';
        }
      }

      return {
        isRunning: connectedTools.some(tool => tool.status === 'connected'),
        connectedTools,
        overallStatus,
        lastUpdated: new Date(),
        error: null
      };
    } catch (error) {
      console.error('Failed to fetch MCP connection status:', error);
      return {
        isRunning: false,
        connectedTools: [],
        overallStatus: 'disconnected',
        lastUpdated: new Date(),
        error: null
      };
    }
  }

  /**
   * Get MCP Host logs via AA backend
   */
  async getLogs(lines: number = 100): Promise<string[]> {
    try {
      const response = await apiClient.get(`/mcp/logs?lines=${lines}`);
      
      if (response.data?.success) {
        return response.data.data || [];
      }
      return [];
    } catch (error) {
      console.error('Failed to fetch MCP Host logs:', error);
      return [];
    }
  }

  /**
   * AUTO-CONNECT: Connect user's enabled MCP servers on login
   * This is the key function that implements the auto-connect feature
   */
  async connectUserMCPServers(tenantId: string): Promise<{ success: boolean; message: string; details?: any }> {
    try {
      // Call the new auto-connect endpoint
      const response = await apiClient.post(`/api/external-tools/auto-connect/${tenantId}`);
      
      if (response.data?.success) {
        return {
          success: true,
          message: response.data.message || 'MCP servers connected successfully',
          details: response.data.data
        };
      } else {
        return {
          success: false,
          message: response.data?.message || 'Failed to auto-connect MCP servers'
        };
      }
    } catch (error) {
      console.error('Failed to auto-connect MCP servers:', error);
      return {
        success: false,
        message: 'Failed to auto-connect MCP servers'
      };
    }
  }

  /**
   * Create an MCP connection log entry for tracking
   */
  async logMCPConnection(tenantId: string, serverName: string, status: 'connected' | 'failed', details?: any): Promise<void> {
    try {
      await apiClient.post('/api/external-tools/connection-log', {
        tenant_id: tenantId,
        server_name: serverName,
        connection_status: status,
        connection_details: details || {},
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Failed to log MCP connection:', error);
    }
  }
}

export const mcpHostService = new MCPHostService();